// Port of ios/Sources/ViewModels/MentionHighlight.swift.
//
// Finds @mentions in ALREADY-RENDERED message text (after `InlineMarkdown` has stripped its
// syntax), so the view layer can colour them. Pure, and deliberately separate from the Markdown
// parser: mentions are not markup — `@ana` inside a `code span` is still text the parser must
// preserve verbatim, and gluing the two vocabularies into one grammar helps neither.
//
// Only mentions of KNOWN usernames (plus the server's broadcast keywords) highlight. Colouring
// every "@word" would light up email addresses' local parts and plain typos, teaching the user
// that the highlight means nothing.
//
// WEB: scanned on Swift Characters (swiftCharacters.ts); offsets are UTF-16 code units.

import { specialMentions } from './mentionComposer';
import { characterView, isASCII, isLetter, isNumber, isWhitespace } from './swiftCharacters';

export interface MentionSpan {
  /**
   * UTF-16 offsets into the rendered text (Swift: Character offsets), same coordinate space as
   * `InlineMarkdown`'s `ParsedText.spans`.
   */
  start: number;
  end: number;
  username: string;
  isSelf: boolean;
}

function isUsernameChar(c: string): boolean {
  if (!isASCII(c)) return false;
  return isLetter(c) || isNumber(c) || c === '.' || c === '_' || c === '-';
}

function spans(args: {
  text: string;
  knownUsernames: ReadonlySet<string>;
  currentUsername: string | null | undefined;
}): MentionSpan[] {
  const { text, knownUsernames } = args;
  const currentUsername = args.currentUsername ?? null;
  if (text === '' || (knownUsernames.size === 0 && currentUsername === null)) return [];
  // A message with no "@" in it cannot contain a mention, and this is nearly every message.
  // The guard is here for what the two lines below cost: the union allocates a fresh Set of
  // every known username in the workspace (hundreds of strings), and the Character view
  // materialises every Character — both once per message per render pass. Paying that for a
  // message that plainly has no mention was a measurable share of the desktop's scroll
  // budget (2026-09-08 Mac performance pass).
  if (!text.includes('@')) return [];
  const known = new Set<string>(knownUsernames);
  for (const special of specialMentions) known.add(special);
  const { chars, offsets } = characterView(text);
  const result: MentionSpan[] = [];
  let i = 0;
  while (i < chars.length) {
    if (chars[i] !== '@') {
      i += 1;
      continue;
    }
    // Same boundary rule as the composer: an "@" glued to preceding text is an email or
    // handle fragment, not a mention.
    if (i > 0 && !isWhitespace(chars[i - 1]) && chars[i - 1] !== '(') {
      i += 1;
      continue;
    }
    let j = i + 1;
    while (j < chars.length && isUsernameChar(chars[j])) j += 1;
    if (!(j > i + 1)) {
      i += 1;
      continue;
    }
    const username = chars.slice(i + 1, j).join('');
    if (known.has(username)) {
      result.push({
        start: offsets[i],
        end: offsets[j],
        username,
        // Broadcast keywords address everyone — including you — but "self" styling is
        // reserved for your NAME: @channel lighting up like a personal call-out on
        // every message would drown the one signal worth having.
        isSelf: currentUsername !== null && username === currentUsername,
      });
    }
    i = j;
  }
  return result;
}

export const MentionHighlight = {
  spans,
} as const;
